import { createActionGroup, emptyProps, props } from '@ngrx/store';

import { ActionSource } from 'src/app/state/action-sources';

import { BlueSageRefundReason } from './bluesage-refund-dialog.state';

export const BlueSageRefundDialogActions = createActionGroup({
    source: ActionSource.BLUESAGE_REFUND_DIALOG,
    events: {
        // Opened from the 3-dot menu on a transaction row. Everything the dialog
        // needs travels with the action: the row already has it, and re-reading
        // it would let the dialog disagree with the row that opened it.
        'Refund Menu Item Clicked': props<{
            transactionId: string;
            originalMinor: number;
            refundableMinor: number;
            channel: string | undefined;
            cardLast4: string | undefined;
            provider: string | undefined;
            /**
             * The differential the SALE was charged, in minor units — 0 when the
             * location runs no dual pricing.
             *
             * Carried so the dialog can QUOTE what the customer will actually
             * receive. The gross-up itself is server-side and this figure never
             * decides it; a client that lied here would change the sentence on
             * screen and nothing about the money.
             */
            saleDifferentialMinor: number;
            /**
             * What the card was actually charged for the sale, in minor units —
             * base plus differential plus any tip and tax. Equal to the base
             * when nothing was added.
             *
             * Quoting only, and only for the FULL-refund case: that path sends
             * no amount and the rail reverses the whole capture, so the figure
             * cannot be derived from the base and the differential alone.
             */
            salePaidMinor: number;
            /**
             * The tip on the sale, in minor units — 0 when there was none.
             *
             * It decides whether the "also refund the tip" choice is offered at
             * all: with no tip there is nothing to withhold and the question
             * would be noise.
             */
            saleTipMinor: number;
        }>(),

        'Amount Changed': props<{ amountMinor: number }>(),

        // Its own event rather than a field on confirm: giving a customer their
        // tip back is a decision an operator makes, and the devtools log should
        // show it being made.
        'Refund Tip Toggled': props<{ refundTip: boolean }>(),

        // Two separate events, because they are two separate decisions: picking
        // the category and writing what happened. Folding them into one
        // `reasonChanged` would make the devtools log unreadable — every
        // keystroke would look like a category change.
        'Reason Category Changed': props<{ reasonCategory: BlueSageRefundReason }>(),
        'Reason Detail Changed': props<{ reasonDetail: string }>(),

        /** One click, because the refusal handed us the exact figure. */
        'Refund Full Instead Button Clicked': emptyProps(),

        'Confirm Button Clicked': emptyProps(),
        'Refund Success': props<{
            rail: string | undefined;
            /**
             * Carried rather than read back from this dialog's state: the effect
             * that follows the leg is registered at the root, and this feature
             * only exists on the job page.
             */
            transactionId: string;
            /**
             * Requirement: a BlueSage refund answers as soon as its leg exists and
             * settles in the background, so "success" here means SENT, not done.
             * The dialog then follows the leg — and to tell this attempt's leg
             * from an earlier partial's, it needs the legs that already existed
             * when Confirm was pressed.
             */
            knownLegIds: string[];
            /** False for a payment taken elsewhere: that refund is only a record, and is final at once. */
            awaitsConfirmation: boolean;
        }>(),
        'Refund Error': props<{ error: Error }>(),

        // BlueSage REFUSED the refund after it was sent — nothing moved — so the
        // operator goes back to the form with what they typed still in it.
        'Try Again Button Clicked': props<{ message: string }>(),

        'Dialog Closed': emptyProps(),
    },
});
